<template>
  <section id="BARRING_FACTORING_STATUS">
    <transition name="fade" mode="out-in" appear>
      <VSpinner v-if="isLoading" class="offset-top-2" />
      <div v-else class="offset-top-2">
        <div v-if="isSuccess" class="container-fixed notification notification-dismissible notification-positive">
          <button type="button" class="close" @click="$emit('update:isSuccess', false)">
            <VSvgIcon name="cancel" variant="outlined" />
            <span class="sr-only">{{ $t('common.close') }}</span>
          </button>
          <div class="notification-left">
            <div class="icon-frame">
              <VSvgIcon name="confirm" variant="outlined" size="large" />
            </div>
          </div>
          <div class="notification-content">
            <div class="notification-heading offset-top-1">
              {{ $t('barring.noticeSaveSuccessful') }}
            </div>
          </div>
        </div>

        <VLayout :enable-tabs="checkEnableTabs" :profile-type="isProfileTypeCategories ? 'category' : 'partner'" :active-tab="activeType" @select="onTabSelect">
          <template #confirmation>
            <VStatusConfirmation :is-blocked="isBlocked" :type="activeType" @confirm="onConfirmTypeMismatchEdit" />
          </template>

          <template #partners>
            <section role="list" class="offset-top-2 offset-bottom-3">
              <VStatus role="listitem" type="factoring" :is-barred="isFactoringBarred" :items="factoringItems" />
              <VStatus role="listitem" type="voiceFactoring" :is-barred="isVoiceFactoringBarred" :items="voiceFactoringItems" />
              <VStatus
                v-if="hasVoiceFactoringExceptions"
                role="listitem"
                type="exceptions"
                :is-barred="isVoiceFactoringBarred"
                :items="voiceFactoringExceptions"
              />
            </section>

            <div class="pull-left offset-left-1 button-bar">
              <VButton
                id="editBarringProfile"
                data-test="edit"
                label="barring.editBarringProfile"
                class="btn-brand"
                :disabled="isBlocked"
                @click="onEdit('edit')"
              />
              <label class="offset-left-1" for="editBarringProfile">
                <template v-if="isBlocked">
                  {{ $t('barring.profileBlocked') }}
                </template>
                <template v-else>
                  {{ $t('barring.buttonAdjacentTextTypePartner') }}
                </template>
              </label>
            </div>
            <p class="clearfix"></p>
          </template>

          <template #categories>
            <section role="list" class="offset-top-2 offset-bottom-3">
              <VStatus role="listitem" type="category" :is-barred="isFactoringBarred" :items="itemsWithoutExceptions" />
              <VStatus
                v-if="itemsWithExceptions.length"
                role="listitem"
                type="category-exceptions"
                :is-barred="isFactoringBarred"
                :items="itemsWithExceptions"
              />
            </section>

            <div class="pull-left offset-left-1 button-bar">
              <VButton
                id="editBarringProfile2"
                data-test="edit"
                label="barring.editBarringProfile"
                class="btn-brand"
                :disabled="isBlocked"
                @click="onEdit('edit')"
              />
              <label class="offset-left-1" for="editBarringProfile2">
                <template v-if="isBlocked">
                  {{ $t('barring.profileBlocked') }}
                </template>
                <template v-else>
                  {{ $t('barring.buttonAdjacentTextTypeCategory') }}
                </template>
              </label>
            </div>
            <p class="clearfix"></p>
          </template>
        </VLayout>
      </div>
    </transition>
  </section>
</template>

<script>
import { mapGetters } from 'vuex';
import emitter from '@/plugins/emitter';
import { BARRING_FACTORING_EDIT, ERROR } from '@route-types';
import VStatusConfirmation from '@/components/Barring/Status/Confirmation';
import TitleMixin from '@/mixins/title';
import BarringStatusMixin from '@/mixins/barringStatus';
import Proxy from '@/proxies/BarringProfileFactoringProxy';
import VSpinner from '@/components/Spinner';
import VButton from '@/components/Button';
import VLayout from '@/components/Barring/Status/FactoringLayout';
import VSvgIcon from '@/components/UI/VSvgIcon.vue';
export default {
  name: 'BarringFactoringStatus',

  components: {
    VSvgIcon,
    VSpinner,
    VButton,
    VStatusConfirmation,
    VLayout,
  },

  mixins: [TitleMixin, BarringStatusMixin],

  props: {
    isSuccess: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isLoading: true,
      activeType: 'partner',
    };
  },

  computed: {
    ...mapGetters('system', ['enableBarringProfileOfTypeCategory']),

    checkEnableTabs() {
      return this.enableBarringProfileOfTypeCategory && !this.isBlocked;
    },
  },

  async created() {
    await this.load();
    emitter.on('navigationReload', this.onReload);
  },

  beforeDestroy() {
    emitter.off('navigationReload', this.onReload);
  },

  methods: {
    async load() {
      const response = await new Proxy().status();
      this.parseResponse(response);

      if (this.isProfileTypeCategories) {
        this.activeType = 'category';
      }

      this.isLoading = false;
    },

    async onReload() {
      this.isLoading = true;
      try {
        await this.load();
      } catch (error) {
        this.$router.push({ name: ERROR, params: { error } });
      }
    },

    onTabSelect(tab) {
      this.activeType = tab;
    },

    async onEdit(action) {
      const response = await new Proxy()[action]();
      this.$store.dispatch('barring/setEditItems', response.items);
      this.$store.dispatch('barring/setProfileTypeCategories', response.isProfileTypeCategories);
      this.$store.dispatch('barring/setHasPin', response.hasPin);

      this.$router.push({ name: BARRING_FACTORING_EDIT, params: { edit: true } });
    },

    async onConfirmTypeMismatchEdit() {
      this.onEdit(this.isProfileTypeCategories ? 'newProfileTypePartners' : 'newProfileTypeCategories');
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@scss';
</style>
